import React, { useEffect, useMemo } from "react";
import { createPortal } from "react-dom";

/**
 * CelebrationOverlay (EPIC)
 * - Fullscreen by default (portals into <body>)
 * - Lots of confetti, multi-layer for smoother rendering
 * - Party-popper scales in, wiggles, then settles
 * - Big animated headline
 *
 * Props:
 * - show: boolean
 * - message?: string
 * - duration?: number (ms) – overlay auto-clears via onDone after this
 * - onDone?: () => void
 * - fullScreen?: boolean (default true)
 * - intensity?: "lite" | "normal" | "epic" (confetti amount; default "epic")
 */
export default function CelebrationOverlay({
  show,
  message = "You nailed it! 🎉",
  duration = 3000,
  onDone,
  fullScreen = true,
  intensity = "epic",
}) {
  // Confetti density
  const pieceTarget = useMemo(() => {
    switch (intensity) {
      case "lite": return 120;     // gentle
      case "normal": return 400;   // festive
      default: return 1200;        // EPIC (~30x the original 40)
    }
  }, [intensity]);

  // Split across layers (CSS animations start at different offsets)
  const layers = useMemo(() => {
    const LAYERS = 6;
    const sizes = new Array(LAYERS).fill(0).map((_, i) => {
      // front layers get fewer pieces to reduce overlap
      const weight = 1 - i * 0.1;
      return Math.max(40, Math.round((pieceTarget / LAYERS) * weight));
    });
    return sizes;
  }, [pieceTarget]);

  useEffect(() => {
    if (!show || !duration) return;
    const t = setTimeout(() => onDone?.(), duration);
    return () => clearTimeout(t);
  }, [show, duration, onDone]);

  if (!show) return null;

  const overlay = (
    <div
      className={
        fullScreen
          ? "fixed inset-0 z-[10000] pointer-events-none"
          : "absolute inset-0 z-50 pointer-events-none"
      }
      aria-hidden
    >
      {/* subtle dim */}
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-black/10" />

      {/* Confetti layers */}
      {layers.map((count, i) => (
        <ConfettiLayer key={i} count={count} depth={i} />
      ))}

      {/* Center stage: Party popper + headline */}
      <div className="absolute inset-0 flex items-center justify-center">
        <div className="flex flex-col items-center gap-4 md:gap-6">
          {/* Party popper */}
          <div
            className="popper pointer-events-auto select-none"
            aria-hidden
          >
            <span role="img" aria-label="party popper">🎉</span>
          </div>

          {/* Headline */}
          <div className="headline pointer-events-auto text-center">
            <div className="text-white/95 drop-shadow-[0_4px_16px_rgba(0,0,0,0.35)] font-extrabold
                            text-2xl sm:text-3xl md:text-5xl lg:text-6xl">
              {message}
            </div>
          </div>
        </div>
      </div>

      {/* Styles */}
      <style>{`
        /* Popper: pop-in, wiggle, settle */
        .popper {
          font-size: clamp(72px, 14vw, 180px);
          transform-origin: center;
          animation:
            pop-in 500ms cubic-bezier(.22,1.26,.21,1) 0ms both,
            wiggle 1600ms ease-in-out 450ms 1 both,
            settle 400ms ease 2050ms 1 both;
        }
        @keyframes pop-in {
          0%   { transform: scale(0);   filter: drop-shadow(0 0 0 rgba(0,0,0,0)); }
          70%  { transform: scale(1.15); filter: drop-shadow(0 16px 30px rgba(0,0,0,.25)); }
          100% { transform: scale(1);    filter: drop-shadow(0 10px 20px rgba(0,0,0,.2)); }
        }
        @keyframes wiggle {
          0%   { transform: rotate(0deg) scale(1); }
          20%  { transform: rotate(-12deg) scale(1.02); }
          40%  { transform: rotate(8deg)  scale(1.01); }
          60%  { transform: rotate(-6deg) scale(1.01); }
          80%  { transform: rotate(3deg)  scale(1); }
          100% { transform: rotate(0deg)  scale(1); }
        }
        @keyframes settle {
          from { transform: translateY(-2px); }
          to   { transform: translateY(0); }
        }

        /* Headline entrance */
        .headline {
          animation: headline-in 600ms cubic-bezier(.22,1.26,.21,1) 120ms both;
        }
        @keyframes headline-in {
          0%   { transform: translateY(20px) scale(.96); opacity: 0; }
          60%  { transform: translateY(-4px) scale(1.04); opacity: 1; }
          100% { transform: translateY(0) scale(1); }
        }

        /* Confetti keyframes */
        @keyframes confetti-fall {
          0%   { transform: translateY(-10vh) rotate(var(--r0)) scale(var(--s)); opacity: 1; }
          80%  { opacity: 1; }
          100% { transform: translateY(110vh) rotate(var(--r1)) scale(var(--s)); opacity: 0; }
        }
      `}</style>
    </div>
  );

  return fullScreen ? createPortal(overlay, document.body) : overlay;
}

// One confetti layer with N pieces; depth controls speed/blur
function ConfettiLayer({ count, depth }) {
  const pieces = useMemo(() => {
    return Array.from({ length: count }).map((_, i) => {
      const left = Math.random() * 100;         // start X
      const delay = Math.random() * 0.9;        // 0–0.9s
      const dur = 1.8 + Math.random() * 1.6;    // 1.8–3.4s
      const scale = 0.7 + Math.random() * 1.0;  // piece size
      const r0 = Math.floor(Math.random() * 360) + "deg";
      const r1 = (360 + Math.floor(Math.random() * 720)) + "deg"; // add spins
      const width = Math.random() < 0.25 ? 6 : 4;
      const height = Math.random() < 0.25 ? 16 : 12;
      // color palette
      const colors = ["#F87171", "#60A5FA", "#FBBF24", "#34D399", "#A78BFA", "#fb7185", "#f59e0b"];
      const color = colors[Math.floor(Math.random() * colors.length)];
      return { left, delay, dur, scale, r0, r1, width, height, color, key: i };
    });
  }, [count]);

  const blur = Math.max(0, depth - 2) * 1.5;     // farther layers slightly blurred
  const speedFactor = 1 + depth * 0.15;          // deeper layers fall slower

  return (
    <div className="absolute inset-x-0 top-0 overflow-visible" style={{ filter: blur ? `blur(${blur}px)` : "none" }}>
      {pieces.map(p => (
        <span
          key={p.key}
          style={{
            left: `${p.left}%`,
            animationDelay: `${p.delay}s`,
            animationDuration: `${(p.dur * speedFactor).toFixed(2)}s`,
            width: p.width,
            height: p.height,
            background: p.color,
            transform: `translateY(-10vh) rotate(${p.r0}) scale(${p.scale})`,
            // custom props for keyframes
            ["--r0"]: p.r0,
            ["--r1"]: p.r1,
            ["--s"]: p.scale,
          }}
          className="absolute -top-8 rounded-sm animate-[confetti-fall_linear_forwards]"
        />
      ))}
    </div>
  );
}